/**
 * Keyboard shortcut engine types.
 *
 * Shortcuts come in layers. Per key combo, the highest layer wins:
 *   user screen > user global > built-in screen > built-in global
 * A modal on top of the stack hides everything except its own commands and
 * those flagged `allowInModal`.
 */

/** 'global', a screen id (ScreenIdEnum value from route data.screen) or `modal:<name>`. */
export type ScopeId = string

export const GLOBAL_SCOPE = 'global'

export type Layer = 'global' | 'screen'

export interface ShortcutCommand {
    /** Stable and namespaced, e.g. 'global.open-cheat-sheet', 'grid.refresh'. Persisted in user prefs. */
    id: string
    /** i18n key or plain text shown in the cheat-sheet and settings page. */
    label: string
    group?: string
    /** Canonical combos (see key-combo.ts), e.g. ['mod+k']. */
    defaultKeys: Array<string>
    run: (e?: KeyboardEvent) => void
    /** Extra runtime guard; the command is skipped (falls through) when it returns false. */
    when?: () => boolean
    /** Defaults to true when the combo includes mod/ctrl/meta/alt, false otherwise (scanner safety). */
    allowInInputs?: boolean
    allowInModal?: boolean
}

/** A command after registration, tagged with where it lives. */
export interface RegisteredCommand extends ShortcutCommand {
    scope: ScopeId
}

export type UserActionType = 'navigate' | 'command' | 'grid-view' | 'focus' | 'macro'

export interface UserAction {
    type: UserActionType
    payload: Record<string, unknown>
}

/** A user's own binding. Either rebinds a built-in command or defines a new action. */
export interface UserBinding {
    id?: number
    scope: ScopeId
    keys: string
    commandId?: string | null
    action?: UserAction | null
    label?: string | null
}

/** Omitted field = wildcard. A candidate is disabled when any rule matches it. */
export interface DisableRule {
    command?: string
    layer?: Layer
    on?: ScopeId
}

export interface ShortcutSettings {
    enabled: boolean
    rules: Array<DisableRule>
}

export interface ShortcutPreferences {
    settings: ShortcutSettings
    bindings: Array<UserBinding>
}

export const DEFAULT_PREFERENCES: ShortcutPreferences = {
    settings: { enabled: true, rules: [] },
    bindings: []
}

/** One resolved entry of the effective key map, used for dispatch and the cheat-sheet. */
export interface EffectiveShortcut {
    keys: string
    commandId: string
    label: string
    group?: string
    scope: ScopeId
    layer: Layer
    source: 'default' | 'user'
    run: (e?: KeyboardEvent) => void
    when?: () => boolean
    allowInInputs: boolean
    allowInModal: boolean
}
